import {Box, Typography} from '@mui/material';
import {
  GridFeatureMode,
  GridFilterItem,
  GridFilterModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {t} from 'i18next';
import {flatMap, isArray} from 'lodash';
import buildQuery, {Filter, raw} from 'odata-query';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {TABLE_PAGE_SIZE_OPTION} from '../../../../constants/constants';
import {isEmpty} from '../../../../utils/utils';
import AurButton from '../../../common/aur-button/AurButton';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridNoRowsOverlay from '../../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurDataGridPro from '../../../common/aur-data-grid-pro/AurDataGridPro';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import AurInfographicSeperator from '../../../common/aur-infographic/AurInfographicSeperator';
import AurPagination from '../../../common/aur-pagination/AurPagination';
import {FieldCategoryType} from '../custom-field/FieldView';
import {FieldFilter} from '../FieldManagement';
import {FieldGridFilterType} from './FieldPopoverGridFilter';
import getFieldTableColumnConfig from './FieldTableColumnConfig';
import {
  FieldCategoryMappingByValue,
  FieldUITypeMappingByValue,
  IFieldModal,
} from '../custom-field/constant';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';

dayjs.extend(utc);

interface FieldTableContainerProps {
  data: IFieldModal[];
  totalItems: number; // Total number of items to paginate through
  itemsPerPage: number; // Number of items per page
  currentPage: number;
  onPageChange: (page: number) => void; // Function to handle page change
  setItemsPerPage: (itemsPerPage: number) => void;
  isReqLoading: boolean;
  haveReqError: boolean;
  onErrorRefresh: () => void;
  isDownloading: boolean;
  onExportGridData: () => void;
  onSetSortModel: (sortModel: GridSortModel) => void;
  sortModel: GridSortModel;
  onClickTableRow?: (selectedField: IFieldModal) => void;
  onChangeOdataParam?: (
    odataUrlParam: string,
    localFilter?: FieldFilter
  ) => void;
}

const FIELD_TYPE_OBJ: Record<FieldGridFilterType, (keyof IFieldModal)[]> = {
  text: ['fieldLabel', 'updatedBy', 'createdBy'],
  options: ['fieldCategoryLabel', 'fieldUIType'],
  date: ['updatedOn', 'createdOn'],
};

const DATE_FILTER_ODATA_MAPPING_OBJ: Record<string, string> = {
  is: 'eq',
  before: 'lt',
  onOrBefore: 'le',
  after: 'gt',
  onOrAfter: 'ge',
};

/* Please change to 'server' once odata params works in api */

const DATA_TABLE_FEATURE_MODE: GridFeatureMode = 'server' as GridFeatureMode;

const FieldTableContainer: React.FC<FieldTableContainerProps> = (
  props: FieldTableContainerProps
) => {
  const {
    data: resData = [],
    totalItems,
    itemsPerPage,
    currentPage,
    onPageChange,
    setItemsPerPage,
    isReqLoading,
    haveReqError,
    onErrorRefresh,
    onExportGridData,
    isDownloading,
    onClickTableRow,
    onSetSortModel,
    sortModel,
    onChangeOdataParam,
  } = props;

  const [data, setData] = useState<IFieldModal[]>([]);

  const isFilterSortingApplied = useRef(false);

  useEffect(() => {
    setData(resData);
  }, [resData]);

  const apiRef = useGridApiRef();

  const [filterCount, setFilterCount] = useState<number>(0);

  const includesFieldInFieldType = useCallback(
    (fieldType: FieldGridFilterType, field: keyof IFieldModal): boolean =>
      FIELD_TYPE_OBJ?.[fieldType]?.includes(field) ?? false,
    []
  );

  const getTextOdataParam = useCallback((filterItem: GridFilterItem) => {
    const {field, value, operator} = filterItem;
    return {
      contains: `contains(${field},'${value}')`,
      doesNotContain: {not: [`contains(${field},'${value}')`]},
      equals: {[field]: {eq: value}},
      doesNotEqual: {[field]: {ne: value}},
      startsWith: `startswith(${field},'${value}')`,
      endsWith: `endswith(${field},'${value}')`,
      isEmpty: {
        or: [
          {
            [field]: {
              eq: '',
            },
          },
          {
            [field]: {
              eq: null,
            },
          },
        ],
      },
      isNotEmpty: {
        and: [
          {
            [field]: {
              ne: '',
            },
          },
          {
            [field]: {
              ne: null,
            },
          },
        ],
      },
    }?.[operator];
  }, []);

  const getDateOdataParam = useCallback((filterItem: GridFilterItem) => {
    const {field, operator, value} = filterItem;
    const odataDateOperator: string = DATE_FILTER_ODATA_MAPPING_OBJ?.[operator];

    return {
      [field]: {
        [odataDateOperator]: raw(dayjs(value).format('YYYY-MM-DD')),
      },
    };
  }, []);

  const buildOdataURLParam = useCallback(() => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;
    const sortModel: GridSortModel = apiRef.current.state.sorting.sortModel;

    const filter: Filter<IFieldModal> = [];
    let odataFilterParams = '';
    let odataSortParams = '';

    if (!isEmpty(filterModel?.items ?? [])) {
      const filterItemArray: GridFilterItem[] = filterModel?.items ?? [];
      filterItemArray.forEach((item: GridFilterItem) => {
        const {field, value} = item;
        const dataField: keyof IFieldModal = field as keyof IFieldModal;
        switch (true) {
          case includesFieldInFieldType('text', dataField): {
            filter.push(getTextOdataParam(item));
            break;
          }
          case includesFieldInFieldType('options', dataField): {
            if (dataField === 'fieldUIType') {
              const typeIds = flatMap(
                value.map((item: never) => FieldUITypeMappingByValue[item])
              );
              const typeFilter = typeIds.map((id) => ({['fieldTypeId']: {eq: id}}));
              filter.push({or: typeFilter});
            } else if (dataField === 'fieldCategoryLabel') {
              const typeFilter = [];
              for (const id of value) {
                const filterFieldCategory =
                  FieldCategoryMappingByValue[id as FieldCategoryType];
                typeFilter.push(
                  isArray(filterFieldCategory)
                    ? {and: filterFieldCategory}
                    : filterFieldCategory
                );
              }
              filter.push({or: typeFilter});
            } else {
              filter.push({
                [field]: {in: value},
              });
            }
            break;
          }
          case includesFieldInFieldType('date', dataField): {
            filter.push(getDateOdataParam(item));
            break;
          }
        }
      });
    }

    if (!isEmpty(sortModel ?? [])) {
      odataSortParams = sortModel
        .filter((sort) => !isEmpty(sort.field))
        .map((sort) => `${sort.field} ${sort.sort}`)
        .join(', ');
    }

    let odataParams: Record<string, string> = {};

    if (!isEmpty(filter)) {
      odataFilterParams = buildQuery({filter});
      odataFilterParams = odataFilterParams.replace(/^\?\$filter=/, '');
      odataParams = {
        ...odataParams,
        filter: odataFilterParams,
      };
    }

    if (!isEmpty(odataSortParams)) {
      odataParams = {
        ...odataParams,
        orderby: odataSortParams,
      };
    }

    if (!isEmpty(odataParams)) {
      isFilterSortingApplied.current = true;
    }

    if (
      isFilterSortingApplied.current &&
      DATA_TABLE_FEATURE_MODE === 'server'
    ) {
      const urlParams: string = new URLSearchParams(odataParams).toString();
      onChangeOdataParam?.(urlParams);
    }
  }, [
    apiRef,
    getDateOdataParam,
    getTextOdataParam,
    includesFieldInFieldType,
    onChangeOdataParam,
  ]);

  const handleFilterChange = useCallback(() => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    const uniqueFields = new Set(filterModel.items.map((item) => item.field));
    setFilterCount(uniqueFields.size);

    buildOdataURLParam();
  }, [apiRef, buildOdataURLParam]);

  useEffect(() => {
    if (!apiRef?.current) return;

    const unsubscribeFilter = apiRef.current.subscribeEvent(
      'filterModelChange',
      handleFilterChange
    );

    const unsubscribeSort = apiRef.current.subscribeEvent(
      'sortModelChange',
      buildOdataURLParam
    );

    return () => {
      unsubscribeFilter();
      unsubscribeSort();
    };
  }, [apiRef, buildOdataURLParam, handleFilterChange]);

  const handleRowClick = useCallback(
    (rowData: IFieldModal) => {
      onClickTableRow?.(rowData);
    },
    [onClickTableRow]
  );

  const ecaFieldColumns = getFieldTableColumnConfig(
    apiRef,
    currentPage,
    itemsPerPage,
    handleRowClick
  );

  const handleClearFilters = useCallback(() => {
    apiRef.current?.setFilterModel({items: []});
  }, [apiRef]);

  const noDataGridOverlay = useMemo(() => {
    if (isReqLoading) return AurDataGridLoadingOverlay;

    if (haveReqError) return AurDataGridErrorOverlay;

    if (filterCount > 0 && data.length <= 0) return AurDataGridNoRowsOverlay;

    return AurDataGridNoRowsOverlay;
  }, [
    isReqLoading,
    haveReqError,
    filterCount,
    data.length,
  ]) as React.JSXElementConstructor<unknown>;

  const renderPaginationBlock = () => {
    return (
      <Box className={'table-pagination-container'}>
        <Box className={'table-pagination-control'}>
          <AurPagination
            totalItems={totalItems}
            itemType={t('fieldManagement.fieldTable.fields')}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            perPageOptions={TABLE_PAGE_SIZE_OPTION}
            onPageChange={onPageChange}
            setItemsPerPage={setItemsPerPage}
            ariaLabelNextPage={t('common.pagination.nextHint')}
            ariaLabelPreviousPage={t('common.pagination.previousHint')}
          />
        </Box>
        {filterCount > 0 ? (
          <>
            <Box>
              <AurInfographicSeperator seperatorIcon={'VerticalLine'} />
            </Box>
            <Box
              className={'apply-filter-button-container'}
              onClick={handleClearFilters}
              data-testid={'apply-clear-filter'}
            >
              <AurButton
                type={'exit'}
                size={'Small'}
                startIcon={<AurIcon icon={IconNames.ClearFiltersIcon} />}
                label={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
              />
            </Box>
          </>
        ) : null}
      </Box>
    );
  };

  return (
    <Box className={'aur-field-table-container'}>
      <Box className={'aur-field-table-total-count-container'}>
        <Box className={'aur-field-table-total-count-text-container'}>
          <Typography
            className={
              'typo-text subhead2_size aur-field-table-total-count-text'
            }
            tabIndex={0}
          >
            {t('fieldManagement.fieldTable.totalFields')}
          </Typography>
          <Typography
            className={'typo-text body2_size aur-field-table-total-number-text'} tabIndex={0}
          >{`(${totalItems})`}</Typography>
        </Box>
        <AurButton
          ariaLabel={`${t('fieldManagement.fieldTable.exportGrid')} ${t('eca.overlay.exportGridHint')}`}
          data-testid="export-button"
          onClick={onExportGridData}
          startIcon={
            isDownloading ? (
              <div className="color-loader">
                <AurLoadingIndicator
                  size={16}
                  className="progress-icon"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <AurIcon icon={IconNames.ExportGridIcon} />
            )
          }
          disabled={isDownloading}
          type="exit"
          size="Medium"
          label={t('fieldManagement.fieldTable.exportGrid')}
        />
      </Box>
      {renderPaginationBlock()}
      <Box className={'field-table-master-container'}>
        <Box className={'data-table-container'}>
          <AurDataGridPro
            apiRef={apiRef}
            className={'aur-grid-pro'}
            hideSortIcons={true}
            rows={data}
            columns={ecaFieldColumns}
            getRowId={(row) => `${row.index}-${row.fieldId}`}
            disableColumnSorting={false}
            hideFooter
            hideFooterRowCount
            disableVirtualization
            hideFooterSelectedRowCount
            headerFilters
            disableRowSelectionOnClick
            filterMode={DATA_TABLE_FEATURE_MODE}
            sortingMode={DATA_TABLE_FEATURE_MODE}
            enableServerSide={DATA_TABLE_FEATURE_MODE === 'server'}
            noRowOverlayClass={'no-row-container'}
            isReqLoading={isReqLoading}
            sortModel={sortModel}
            setSortModel={onSetSortModel}
            haveReqError={haveReqError}
            onErrorRefresh={onErrorRefresh}
            data-testid={'str-table'}
            checkBoxVisible={false}
            defaultItemsPerPage={itemsPerPage}
            columnHeaderHeight={48}
            headerFilterHeight={48}
            rowHeight={36}
            onRowClick={(params) => handleRowClick(params.row)}
            visibleToolTip={true}
            slots={{
              headerFilterMenu: null,
              noRowsOverlay: noDataGridOverlay,
              noResultsOverlay: noDataGridOverlay,
            }}
            slotProps={{
              headerFilterCell: {
                showClearIcon: false,
                InputComponentProps: {
                  type: 'search',
                  label: '',
                  placeholder: t('fieldManagement.fieldTable.search'),
                  autoComplete: 'off',
                  tabIndex: '0',
                },
              },
              noRowsOverlay: {
                onRefresh: onErrorRefresh,
                enableParentWrapper: false,
              } as unknown as Record<string, unknown>,
            }}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
        </Box>
      </Box>
      {renderPaginationBlock()}
    </Box>
  );
};

export default FieldTableContainer;
